import Highcharts from 'highcharts/highstock';
import {orderBy} from 'lodash';
import {ChartModel} from '@xh/hoist/cmp/chart';
import {HoistModel, LoadSpec, lookup, managed, PlainObject, SelectOption, XH} from '@xh/hoist/core';
import {DashViewModel} from '@xh/hoist/desktop/cmp/dash';
import {action, bindable, observableRef, runInAction} from '@xh/hoist/mobx';
import {HoldingsTypeEnum, Position, RiskAnalytics, RiskWindow} from '../../../../data/DataTypes';
import {buildChart, SeriesToggle} from './PerformanceCharts';
import {ALL_METRICS, MetricDef, RISK_METRICS} from './PerformanceMetrics';
import {PerformanceWidgetModel} from './PerformanceWidgetModel';

/**
 * The Performance widget: performance and risk figures side by side, of all the non-cash portfolios or one picked
 * portfolio, and a chart of the active metric. Risk metrics are over the picked window. The live figures come from
 * {@link PerformanceWidgetModel}, the risk analytics from the server - see the backend's decision 0056.
 */
export class PerformanceRiskWidgetModel extends HoistModel {
    @lookup(DashViewModel) dashViewModel: DashViewModel;
    @managed readonly figures = new PerformanceWidgetModel();
    @managed readonly chartModel = new ChartModel();

    @observableRef accessor risk: RiskAnalytics = null;
    /** The whole history, for the CAGR chart - loaded when that chart is first shown, unless the window is ALL. */
    @observableRef accessor allRisk: RiskAnalytics = null;
    /** One portfolio, or null for all the non-cash portfolios. */
    @bindable accessor portfolioId: number = null;
    @bindable accessor window: RiskWindow = '1Y';
    @bindable accessor activeMetric: string = 'mv';
    /** The active chart's series, toggled from its toolbar. Empty for a chart without a legend. */
    @observableRef accessor toggles: SeriesToggle[] = [];
    /** The metric the chart was last drawn for. */
    private chartMetric: string = null;

    get activeDef(): MetricDef {
        return ALL_METRICS.find(m => m.id === this.activeMetric) ?? ALL_METRICS[0];
    }

    /** Performance charts toggle their series from the toolbar. On risk charts and the RMD donut it is just a legend. */
    get canToggleSeries(): boolean {
        const def = this.activeDef;
        return !RISK_METRICS.includes(def) && def.chart !== 'rmd';
    }

    get portfolioOptions(): SelectOption[] {
        const portfolios = orderBy(
            XH.portfolioService.portfolios.filter(p => p.isActive && p.holdingsType != HoldingsTypeEnum.Cash),
            'portfolioName'
        );
        return [{value: null, label: 'All portfolios'}, ...portfolios.map(p => ({value: p.id, label: p.portfolioName}))];
    }

    override onLinked() {
        const {dashViewModel} = this;
        this.markPersist('portfolioId', {dashViewModel});
        this.markPersist('window', {dashViewModel});
        this.markPersist('activeMetric', {dashViewModel});

        this.addReaction(
            {
                track: () => [this.portfolioId, this.window],
                run: () => this.loadAsync()
            },
            {
                track: () => [this.activeMetric, this.risk, this.allRisk],
                run: () => this.updateChart(),
                fireImmediately: true
            },
            {
                track: () => [this.activeDef.chart, this.risk],
                run: () => this.loadAllRiskAsync()
            }
        );
    }

    override async doLoadAsync(loadSpec: LoadSpec): Promise<void> {
        const {figures, portfolioId, window} = this;
        figures.portfolioId = portfolioId;

        const [risk] = await Promise.all([XH.performanceService.fetchRiskAnalyticsAsync(portfolioId, window), figures.loadAsync()]);
        if (loadSpec.isStale) return;
        runInAction(() => {
            this.risk = risk;
            this.allRisk = window === 'ALL' ? risk : null;
        });
    }

    private async loadAllRiskAsync() {
        const {portfolioId, risk} = this;
        if (this.activeDef.chart !== 'cagr' || !risk || this.allRisk) return;

        const allRisk = await XH.performanceService.fetchRiskAnalyticsAsync(portfolioId, 'ALL').catchDefault();
        // ignore a reply for a portfolio or window picked away from meanwhile
        if (this.risk === risk) runInAction(() => (this.allRisk = allRisk));
    }

    /** Shows or hides a series of the active chart - clicked via its color-matched toolbar button. */
    @action
    toggleSeries(id: string) {
        const toggle = this.toggles.find(t => t.id === id);
        if (!toggle || !this.canToggleSeries) return;

        const visible = !toggle.visible;
        this.toggles = this.toggles.map(t => (t.id === id ? {...t, visible} : t));
        // a series, or a pie's slice
        (this.chartModel.highchart?.get(id) as Highcharts.Series | Highcharts.Point)?.setVisible(visible, true);
    }

    private updateChart() {
        const {chartModel, risk, allRisk, activeMetric} = this;
        if (!risk) return;

        const {config, series, toggles = []} = buildChart(this.activeDef.chart, {risk, allRisk, cagr: this.figures.calcData?.cagr, positions: this.positions});

        // A reload of the same chart keeps the series hidden from the toolbar - another metric's chart shows them all
        const hidden = activeMetric === this.chartMetric ? this.toggles.filter(t => !t.visible).map(t => t.name) : [];
        toggles.forEach(t => (t.visible = !hidden.includes(t.name)));
        const show = (o: PlainObject) => (o.visible = !hidden.includes(o.name));
        // a pie's toggles are its slices
        series.forEach(s => (s.type === 'pie' ? s.data.forEach(show) : show(s)));

        chartModel.setHighchartsConfig(config);
        chartModel.setSeries(series);
        runInAction(() => (this.toggles = toggles));
        this.chartMetric = activeMetric;
    }

    /** Live positions in scope - the picked portfolio, or every non-cash portfolio. */
    private get positions(): Position[] {
        const {portfolioId} = this;
        return XH.livePositionsService.cube.store.allRecords
            .map(r => r.data as Position)
            .filter(p => (portfolioId != null ? p.portfolioId === portfolioId : p.portfolioHoldingType != HoldingsTypeEnum.Cash));
    }
}
